/** @format */

import { moveItemInArray } from "@angular/cdk/drag-drop"
import { Component } from "@angular/core"
import { ActivatedRoute, Router } from "@angular/router"
import { concat, forkJoin, Observable } from "rxjs"
import { DataPoint } from "src/app/models/datapoint.model"
import { Logsheet } from "src/app/models/logsheet.model"
import { DataPointService } from "src/app/services/data-point/data-point.service"
import { ErrorHandlerService } from "src/app/services/errorHandler/error-handler.service"
import { LogsheetService } from "src/app/services/logsheet/logsheet.service"
import { DialogService } from "src/app/services/modal/dialog.service"
import { SubmissionGuardService } from "src/app/services/submission-guard/submission-guard.service"
import { ToastService } from "src/app/services/toast/toast.service"
import { Roles } from "src/app/auth/roles";
import { IntegrationService } from "src/app/services/integration/integration.service"

interface SelectedField {
	dataPoint: DataPoint
	hiddenByDefault: boolean
	mandatoryWhenVisible: boolean
	requiredWhenVisible: boolean
	sourceDataPointId: number | null
	operator: string
	conditionValue: string
	isDeleted?: boolean
}

interface GuardFormItem {
	id?: number
	label: string
	guardType: 'Advisory' | 'Acknowledged'
	priority: number
	sourceDataPointId: number | null
	operator: string | null
	thresholdValue: string
	warningMessage: string
	expanded: boolean
}

@Component({
	standalone: false,
	selector: 'app-edit-logsheet',
	templateUrl: './edit-logsheet.component.html',
	styleUrls: [
		'./edit-logsheet.component.css',
		'../add-logsheet/add-logsheet.component.css',
	],
})
export class EditLogsheetComponent {
	readonly Roles = Roles;
	selectedValues = {
		company: {
			companyId: 0,
			companyName: 'Company',
		},
		plant: {
			plantId: 0,
			plantName: 'Plant',
		},
		unit: {
			unitId: 0,
			unitName: 'Unit',
		},
	}
	logSheetName: string = ''

	constructor(
		private dataPointService: DataPointService,
		private route: ActivatedRoute,
		private router: Router,
		public dialogService: DialogService,
		public toastService: ToastService,
		private logsheetService: LogsheetService,
		private errorService: ErrorHandlerService,
		private guardService: SubmissionGuardService,
		private integrationService: IntegrationService,
	) {}

	activeDataPoints: DataPoint[] = []
	itemArray: any = []
	selectDataPoints: SelectedField[] = []
	activeStep = 1
	currentRole: any
	errorMessage = {
		success: true,
		message: '',
	}
	firstRender = false
	searchText: String = ''
	logSheetTemplateId: any = ''
	editLogsheetRequest: Logsheet = {
		logSheetTemplateId: 0,
		logSheetName: '',
		isDeleted: false,
		scheduleType: '',
		datapointId: [] as number[],
	}
	showModal: boolean = false
	availableApiEndpoints: any[] = []
	currentAssignments: any[] = []
	assignmentIdMap = new Map<number, number>()
	idsToRemove: number[] = []
	newIdsToAdd: number[] = []
	selectedCloudIntegrations: any[] = []
	cloudOptions: any[] = []
	selectedCloudConnectionId = 0
	showCloudConfig = false
	pickerOpen = false
	pickerScreen: 'category' | 'api-endpoints' = 'category'
	pickerSelectedId: number | null = null
	modalParams = [
		{ label: 'Prefix', value: '' },
		{ label: 'Folder Path', value: '' },
	]

	activeFieldIndex: number | null = null

	guards: GuardFormItem[] = []
	deletedGuardIds: number[] = []

	operatorOptions = [
		{ id: 1, name: "Equal (=)" },
		{ id: 2, name: "Not Equal (!=)" },
		{ id: 3, name: "Greater than (>)" },
		{ id: 4, name: "Less than (<)" },
		{ id: 5, name: "Is Empty" },
		{ id: 6, name: "Is Not Empty" },
	]

	guardOperatorOptions = [
		{ id: "<", name: "Less than (<)" },
		{ id: "<=", name: "Less than or equal (<=)" },
		{ id: ">", name: "Greater than (>)" },
		{ id: ">=", name: "Greater than or equal (>=)" },
		{ id: "==", name: "Equal (=)" },
		{ id: "!=", name: "Not Equal (!=)" },
		{ id: "contains", name: "Contains" },
	]

	get activeField(): SelectedField | null {
		if (this.activeFieldIndex === null) return null
		return this.selectDataPoints[this.activeFieldIndex] ?? null
	}

	get guardSourceFieldOptions() {
		return this.selectDataPoints.map(f => ({
			id: f.dataPoint.dataPointId,
			name: f.dataPoint.dataPointName,
		}))
	}

	get currentAssignmentIds() {
		return this.currentAssignments.map(assignment => assignment.apiEndpointConfigId)
	}

	get pickerEndpoints() {
		const assignedIds = new Set(this.currentAssignmentIds)
		return this.availableApiEndpoints.filter(endpoint => !assignedIds.has(endpoint.apiEndpointConfigId))
	}

	get availableApiEndpointSummary() {
		if (this.availableApiEndpoints.length === 0) {
			return 'No endpoints configured'
		}

		return this.availableApiEndpoints.map(endpoint => endpoint.displayName).join(', ')
	}

	transferData() {
		this.firstRender = true
		this.router.navigateByUrl('settings/forms', {
			state: {
				selectedValues: { ...this.selectedValues },
				firstRender: this.firstRender,
			},
		})
	}

	setActiveStep(step: number) {
		this.activeStep = step
	}

	openPicker() {
		this.pickerOpen = true
		this.pickerScreen = 'category'
		this.pickerSelectedId = null
	}

	closePicker() {
		this.pickerOpen = false
	}

	goToApiEndpoints() {
		this.pickerScreen = 'api-endpoints'
	}

	goToCategory() {
		this.pickerScreen = 'category'
	}

	selectPickerEndpoint(id: number) {
		this.pickerSelectedId = this.pickerSelectedId === id ? null : id
	}

	confirmPickerSelection() {
		if (this.pickerSelectedId === null) {
			this.pickerOpen = false
			return
		}

		const endpoint = this.availableApiEndpoints.find(
			item => item.apiEndpointConfigId === this.pickerSelectedId
		)
		const alreadyAssigned = this.currentAssignments.some(
			item => item.apiEndpointConfigId === this.pickerSelectedId
		)

		if (endpoint && !alreadyAssigned) {
			this.currentAssignments.push({
				formEndpointConfigId: 0,
				logSheetTemplateId: this.logSheetTemplateId,
				apiEndpointConfigId: endpoint.apiEndpointConfigId,
				endpointName: endpoint.displayName,
				endpointUrl: endpoint.url ?? '',
				createdAt: '',
			})
			if (!this.newIdsToAdd.includes(endpoint.apiEndpointConfigId)) {
				this.newIdsToAdd.push(endpoint.apiEndpointConfigId)
			}
		}

		this.pickerSelectedId = null
		this.pickerOpen = false
	}

	removeApiEndpointAssignment(id: number) {
		this.currentAssignments = this.currentAssignments.filter(
			assignment => assignment.apiEndpointConfigId !== id
		)

		const assignmentId = this.assignmentIdMap.get(id)
		if (assignmentId !== undefined) {
			this.idsToRemove.push(assignmentId)
		}
		this.newIdsToAdd = this.newIdsToAdd.filter(endpointId => endpointId !== id)
	}

	navigateToIntegrations() {
		this.router.navigate(['/settings/integrations'])
	}

	selectCloudPickerEndpoint(id: number) {
		this.selectedCloudConnectionId = this.selectedCloudConnectionId === id ? 0 : id
	}

	confirmCloudPickerSelection() {
		this.pickerOpen = false
		this.showCloudConfig = true
	}

	navigateToCloudIntegration() {
		this.router.navigate(['/settings/integrations/cloud-connections'])
	}

	removeCloudAssignment(id: number) {
		this.selectedCloudIntegrations = this.selectedCloudIntegrations.filter(
			integration => integration.cloudIntegrationId !== id
		)
	}

	enterNewCloudConnection() {
		const option = this.cloudOptions.find(
			integration => integration.cloudIntegrationId === this.selectedCloudConnectionId
		)
		const alreadySelected = this.selectedCloudIntegrations.some(
			integration => integration.cloudIntegrationId === this.selectedCloudConnectionId
		)

		if (option && !alreadySelected) {
			this.selectedCloudIntegrations.push({
				...option,
				prefix: this.modalParams[0]?.value ?? '',
				folderPath: this.modalParams[1]?.value ?? '',
			})
		}

		this.showCloudConfig = false
	}

	isSelectedDataPoint(dataPoint: DataPoint) {
		return this.selectDataPoints.findIndex(
			x => x.dataPoint.dataPointId === dataPoint.dataPointId
		) >= 0
	}

	closeModal() {
		this.showModal = false
	}

	addDataPoint(data: DataPoint) {
		this.selectDataPoints.push({
			dataPoint: data,
			hiddenByDefault: false,
			mandatoryWhenVisible: true,
			requiredWhenVisible: false,
			sourceDataPointId: null,
			operator: "Equal (=)",
			conditionValue: "",
		})
	}

	removeDataPoint(index: number) {
		this.selectDataPoints.splice(index, 1)
		if (this.activeFieldIndex === index) {
			this.activeFieldIndex = null
		} else if (this.activeFieldIndex !== null && this.activeFieldIndex > index) {
			this.activeFieldIndex--
		}
	}

	toggleFieldPanel(index: number) {
		this.activeFieldIndex = this.activeFieldIndex === index ? null : index
	}

	getBadgeClass(field: SelectedField): string {
		return field.hiddenByDefault ? "badge-conditional" : "badge-always"
	}

	onFieldBadgeClick(index: number, event: MouseEvent) {
		event.stopPropagation()
		const field = this.selectDataPoints[index]
		if (!field) return

		field.hiddenByDefault = !field.hiddenByDefault
		if (field.hiddenByDefault) {
			field.mandatoryWhenVisible = false
			field.requiredWhenVisible = false
		} else {
			field.sourceDataPointId = null
			field.operator = "Equal (=)"
			field.conditionValue = ""
			field.requiredWhenVisible = false
		}

		this.activeFieldIndex = index
	}

	getSourceFieldOptions(excludeIndex: number) {
		return this.selectDataPoints
			.filter((_, i) => i !== excludeIndex)
			.map(f => ({ id: f.dataPoint.dataPointId, name: f.dataPoint.dataPointName }))
	}

	getSourceFieldLabel(field: SelectedField): string {
		if (!field.sourceDataPointId) return "Select field"
		const found = this.selectDataPoints.find(
			f => f.dataPoint.dataPointId === field.sourceDataPointId
		)
		return found ? found.dataPoint.dataPointName : "Select field"
	}

	onSourceFieldSelect(event: any, field: SelectedField) {
		field.sourceDataPointId = event.id
	}

	onOperatorSelect(event: any, field: SelectedField) {
		field.operator = event.name
	}

	onToggleHiddenByDefault(enabled: boolean) {
		if (this.activeField) {
			this.activeField.hiddenByDefault = enabled
			if (enabled) {
				this.activeField.mandatoryWhenVisible = false
				this.activeField.requiredWhenVisible = false
			} else {
				this.activeField.sourceDataPointId = null
				this.activeField.operator = "Equal (=)"
				this.activeField.conditionValue = ""
				this.activeField.requiredWhenVisible = false
			}
		}
	}

	onToggleMandatoryWhenVisible(enabled: boolean) {
		if (this.activeField) {
			if (enabled) {
				this.activeField.hiddenByDefault = false
				this.activeField.sourceDataPointId = null
				this.activeField.operator = "Equal (=)"
				this.activeField.conditionValue = ""
				this.activeField.requiredWhenVisible = false
			}
			this.activeField.mandatoryWhenVisible = enabled
		}
	}

	onToggleRequiredWhenVisible(enabled: boolean) {
		if (this.activeField && this.activeField.hiddenByDefault) {
			this.activeField.mandatoryWhenVisible = false
			this.activeField.requiredWhenVisible = enabled
		} else if (this.activeField) {
			this.activeField.requiredWhenVisible = false
		}
	}

	private resolveBooleanFlag(value: any, fallback: boolean): boolean {
		if (value === true || value === "true" || value === 1 || value === "1") {
			return true
		}
		if (value === false || value === "false" || value === 0 || value === "0") {
			return false
		}
		return fallback
	}

	private resolveMandatoryWhenVisible(item: any): boolean {
		const mandatoryFlag =
			item?.mandatoryWhenVisible ??
			item?.requiredWhenVisible

		return this.resolveBooleanFlag(mandatoryFlag, true)
	}

	private resolveRequiredWhenVisible(item: any): boolean {
		return this.resolveBooleanFlag(item?.fieldCondition?.requiredWhenVisible, false)
	}

	private normalizeFieldToggles(field: SelectedField): SelectedField {
		if (field.hiddenByDefault) {
			field.mandatoryWhenVisible = false
		}
		if (!field.hiddenByDefault) {
			field.requiredWhenVisible = false
		}
		if (field.requiredWhenVisible && field.mandatoryWhenVisible) {
			field.mandatoryWhenVisible = false
		}
		return field
	}

	// ─── Guard methods ───────────────────────────────────────────────────────────

	addGuard() {
		this.guards.push({
			label: "New Submission Guard",
			guardType: "Acknowledged",
			priority: this.guards.length + 1,
			sourceDataPointId: null,
			operator: null,
			thresholdValue: "",
			warningMessage: "",
			expanded: false,
		})
	}

	removeGuard(index: number) {
		const guard = this.guards[index]
		if (guard.id !== undefined) {
			this.deletedGuardIds.push(guard.id)
		}
		this.guards.splice(index, 1)
		this.guards.forEach((g, i) => (g.priority = i + 1))
	}

	toggleGuard(index: number) {
		this.guards[index].expanded = !this.guards[index].expanded
	}

	selectGuardType(index: number, type: 'Advisory' | 'Acknowledged') {
		this.guards[index].guardType = type
	}

	onGuardSourceFieldSelect(event: any, guard: GuardFormItem) {
		guard.sourceDataPointId = event.id
	}

	onGuardOperatorSelect(event: any, guard: GuardFormItem) {
		guard.operator = event.id
	}

	getGuardSourceFieldLabel(guard: GuardFormItem): string {
		if (!guard.sourceDataPointId) return "Select field"
		const found = this.selectDataPoints.find(
			f => f.dataPoint.dataPointId === guard.sourceDataPointId
		)
		return found ? found.dataPoint.dataPointName : "Select field"
	}

	getGuardOperatorLabel(guard: GuardFormItem): string {
		if (!guard.operator) return "Select function"
		const found = this.guardOperatorOptions.find(o => o.id === guard.operator)
		return found ? found.name : guard.operator
	}

	getGuardWarningPlaceholder(guardType: string): string {
		return guardType === 'Advisory'
			? 'e.g. "The temperature you have submitted ({value}) is above the normal threshold."'
			: 'e.g. "Please confirm you have reviewed this entry before proceeding."'
	}

	areGuardsValid(): boolean {
		return this.guards.every(g => {
			const base =
				g.label.trim().length > 0 &&
				g.priority >= 1 &&
				g.warningMessage.trim().length > 0
			if (g.guardType === 'Advisory') {
				return base &&
					g.sourceDataPointId !== null &&
					!!g.operator &&
					g.thresholdValue.trim().length > 0
			}
			return base
		})
	}

	// ─── Navigation ─────────────────────────────────────────────────────────────

	isValidLogsheetName() {
		return (
			this.editLogsheetRequest.logSheetName.length >= 3 &&
			this.editLogsheetRequest.logSheetName.length <= 50
		)
	}

	enableNextBtn() {
		if (this.activeStep === 1 && !this.isValidLogsheetName()) {
			return false
		}
		if (this.activeStep === 2 && this.selectDataPoints.length === 0) {
			return false
		}
		if (this.activeStep === 3 && !this.areGuardsValid()) {
			return false
		}
		return true
	}

	drop(event: any) {
		moveItemInArray(
			this.selectDataPoints,
			event?.previousIndex,
			event?.currentIndex,
		)
		if (this.activeFieldIndex !== null) {
			const prev = event.previousIndex
			const curr = event.currentIndex
			if (prev === this.activeFieldIndex) {
				this.activeFieldIndex = curr
			} else if (prev < this.activeFieldIndex && curr >= this.activeFieldIndex) {
				this.activeFieldIndex--
			} else if (prev > this.activeFieldIndex && curr <= this.activeFieldIndex) {
				this.activeFieldIndex++
			}
		}
	}

	ngOnInit(): void {
		this.currentRole = localStorage.getItem('role')
		this.selectedValues = history.state?.selectedValues
		this.logSheetName = history?.state?.logSheetName
		this.route.paramMap.subscribe({
			next: (params) => {
				const id = params.get('id')

				if (id) {
					this.logsheetService.retrieveLogsheetRelative(id).subscribe({
						next: (response) => {
							this.logSheetTemplateId = response.logSheetTemplateId
							this.loadApiEndpointAssignments()
							this.logsheetService
								.retrieveLogsheetWithConditions(response.logSheetTemplateId.toString())
								.subscribe({
									next: (response) => {
										this.editLogsheetRequest = response;
										this.itemArray = response?.dataPointDetails || [];

										this.selectDataPoints = this.itemArray.map((item:any) => {
										const mappedField: SelectedField = {
											dataPoint: {
													dataPointId: item.dataPointId,
													dataPointName: item?.dataPointName,
													dataPointDescription: item?.dataPointDescription,
													dataType: item?.dataType || '',
													unitOfMeasurement: item?.unitOfMeasurement || '',
													dataPointMin: item?.dataPointMin || 0,
													dataPointMax: item?.dataPointMax || 0,
													isDeleted: item?.isDeleted || false,
													status: item?.status || '',
													unitId: item?.unitId || 0,
											},
											// map condition directly from backend response
											hiddenByDefault: this.resolveBooleanFlag(
												item?.hiddenByDefault ?? !!item?.fieldCondition,
												false
											),
											mandatoryWhenVisible: this.resolveMandatoryWhenVisible(item),
											requiredWhenVisible: this.resolveRequiredWhenVisible(item),
											sourceDataPointId: item?.fieldCondition?.sourceFieldId ?? item?.sourceDataPointId ?? null,
											operator: item?.fieldCondition?.condition ?? item?.operator ?? "Equal (=)",
											conditionValue: item?.fieldCondition?.value ?? item?.conditionValue ?? "",
											isDeleted: item?.fieldCondition?.isDeleted || false
										};
										return this.normalizeFieldToggles(mappedField)
									});

													// Load existing submission guards
													this.guardService.getGuardsForTemplate(this.logSheetTemplateId).subscribe({
											next: (guards) => {
												this.guards = guards.map((g, i) => ({
													id: g.id,
													label: g.label || `Guard ${i + 1}`,
													guardType: g.guardType,
													priority: g.priority,
													sourceDataPointId: g.sourceDataPointId,
													operator: g.operator,
													thresholdValue: g.thresholdValue ?? '',
													warningMessage: g.warningMessage,
													expanded: false,
												}))
											},
											error: () => {},
										})
									},
									error: (response) => {
										this.errorMessage = this.errorService.handleRetrievalErrors(response);
									},
								});

						},
						error: (response) => {
							this.errorMessage = this.errorService.handleRetrievalErrors(response)
						},
					})
				}
			},
		})
		this.dataPointService
			.retrieveDataPointByUnitId(this.selectedValues.unit.unitId.toString())
			.subscribe({
				next: (dataPoints) => {
					this.activeDataPoints = dataPoints
				},
				error: (err) => {
					this.errorMessage = this.errorService.handleRetrievalErrors(err)
				},
			})

		this.integrationService.retrieveApiIntegrations().subscribe({
			next: (endpoints) => {
				this.availableApiEndpoints = endpoints
			},
			error: (err) => {
				this.errorService.handleError(err, "Error retrieving API integrations")
			},
		})
	}

	private loadApiEndpointAssignments() {
		this.integrationService.getFormEndpointAssignments(this.logSheetTemplateId).subscribe({
			next: (assignments) => {
				this.currentAssignments = assignments
				this.assignmentIdMap = new Map(
					assignments.map((assignment) => [
						assignment.apiEndpointConfigId,
						assignment.formEndpointConfigId,
					])
				)
			},
			error: (err) => {
				this.errorService.handleError(err, "Error retrieving form API integrations")
			},
		})
	}

	private onSaveSuccess() {
		this.transferData()
		this.toastService.showToast = true
		this.toastService.toastText = 'Form successfully updated!'
		setTimeout(() => {
			this.toastService.showToast = false
		}, 5000)
	}

	editLogsheet() {
		const { datapointId: _, ...baseRequest } = this.editLogsheetRequest
		const payload = {
			...baseRequest,
			dataPoints: this.selectDataPoints.map((f, index) => ({
				dataPointId: f.dataPoint.dataPointId,
				order: index,
				hiddenByDefault: f.hiddenByDefault,
				mandatoryWhenVisible: f.mandatoryWhenVisible,
				// Backward-compat for older API contracts that still read top-level requiredWhenVisible.
				requiredWhenVisible: f.mandatoryWhenVisible,
				sourceDataPointId: f.sourceDataPointId,
				operator: f.hiddenByDefault ? f.operator : null,
				conditionValue: f.hiddenByDefault ? f.conditionValue : null,
				fieldCondition: f.hiddenByDefault
					? {
							sourceFieldId: f.sourceDataPointId || 0,
							condition: f.operator || "Equal (=)",
							value: f.conditionValue || "",
							requiredWhenVisible: f.requiredWhenVisible,
					  }
					: null,
			})),
		}

					this.logsheetService.updateLogSheet(this.logSheetTemplateId, payload as any).subscribe({
						next: () => {
							this.saveGuards()
						},
			error: (_res) => {
				this.logsheetService.retrieveLogsheetRelative(this.logSheetTemplateId).subscribe({
					next: (resp: any) => {
						const retryPayload = { ...payload, logSheetTemplateId: resp.logSheetTemplateId }
						this.logsheetService.updateLogSheet(resp.logSheetTemplateId, retryPayload as any).subscribe({
							next: () => {
								this.saveGuards()
							},
							error: (error) => {
								this.errorService.handleError(error, 'Error updating form')
							},
						})
					},
					error: (error) => {
						this.errorService.handleError(error, 'Error updating form')
					},
				})
			},
		})
	}

	private saveApiEndpointAssignments() {
		const removeRequests = this.idsToRemove.map((assignmentId) =>
			this.integrationService.unassignEndpointFromForm(this.logSheetTemplateId, assignmentId)
		)
		const addRequests = this.newIdsToAdd.map((endpointId) =>
			this.integrationService.assignEndpointToForm(this.logSheetTemplateId, endpointId)
		)
		const requests = [...removeRequests, ...addRequests]

		if (requests.length === 0) {
			this.onSaveSuccess()
			return
		}

		forkJoin(requests).subscribe({
			next: () => this.onSaveSuccess(),
			error: (err) => {
				this.toastService.showToast = true
				this.toastService.isError = true
				this.toastService.toastText =
					err?.error?.message || "Error saving API integrations"
				setTimeout(() => {
					this.toastService.showToast = false
				}, 5000)
			},
		})
	}

	private saveGuards() {
		const requests: Observable<any>[] = []

		for (const id of this.deletedGuardIds) {
			requests.push(this.guardService.deleteGuard(id))
		}

		for (const g of this.guards) {
			const body = {
				templateId: this.logSheetTemplateId,
				label: g.label,
				sourceDataPointId: g.sourceDataPointId,
				operator: g.operator,
				thresholdValue: g.thresholdValue || null,
				guardType: g.guardType,
				warningMessage: g.warningMessage,
				priority: g.priority,
				isActive: true,
			}
			if (g.id !== undefined) {
				requests.push(this.guardService.updateGuard(g.id, body))
			} else {
				requests.push(this.guardService.createGuard(body))
			}
		}

		if (requests.length === 0) {
			this.saveApiEndpointAssignments()
			return
		}

		concat(...requests).subscribe({
			complete: () => this.saveApiEndpointAssignments(),
			error: (err) => this.errorService.handleError(err, 'Error saving submission guards'),
		})
	}
}
